import type { ComponentType } from "react";

import { PERMISSION } from "./permission";

export interface AdminSection {
  key: string;
  label: string;
  path: string;
  permission: PERMISSION;
  loader: () => Promise<{ default: ComponentType }>;
}

// A leaf module — imports only `./permission`, so it's safe to read during sidebar.tsx's own
// evaluation (see the comment there). `loader`, not an eager `element`, keeps this file free of any
// container import: an eager reference would make `@shared/constants` (imported by nearly every
// module in the app) transitively pull in every admin section's component tree at init, collapsing
// their route-level code-splitting.
//
// Two invariants AdminContainer relies on and nothing currently checks:
// - Never empty. The outer `<AuthGuard permissions={ADMIN_SECTIONS.map(s => s.permission)}>` in
//   ApplicatonContainer.tsx becomes `permissions={[]}` if this were, which denies EVERY
//   authenticated user (checkPermissions on an empty list is vacuously false) rather than the
//   "no matching section" empty state AdminContainer itself renders for that case.
// - Append-only. `AdminContainer` redirects a bare `/admin` to the first entry here the user
//   holds — reordering existing entries silently changes which section an existing multi-permission
//   user lands on.
export const ADMIN_SECTIONS: AdminSection[] = [
  {
    key: "evaluations",
    label: "Evaluations",
    path: "evaluations",
    permission: PERMISSION.RUN_EVALUATIONS,
    loader: () => import("@containers/Evaluation/containers/EvaluationContainer/EvaluationContainer"),
  },
  {
    key: "procedure-index",
    label: "Procedure Index",
    path: "procedure-index",
    permission: PERMISSION.VIEW_PROCEDURE_INDEX_STATUS,
    loader: () => import("@containers/ProcedureIndex/containers/ProcedureIndexStatusScreen/ProcedureIndexStatusScreen"),
  },
  {
    key: "settings",
    label: "Settings",
    path: "settings",
    permission: PERMISSION.MANAGE_APP_SETTINGS,
    loader: () => import("@containers/Settings/containers/SettingsContainer/SettingsContainer"),
  },
  {
    key: "audit-log",
    label: "Audit log",
    path: "audit-log",
    permission: PERMISSION.VIEW_AUDIT_LOG,
    loader: () => import("@containers/AuditLog/containers/AuditLogContainer/AuditLogContainer"),
  },
];
